import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { GridColDef } from "@mui/x-data-grid";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { Alert } from "@mui/material";

import "../../../../css/forms.css";
import "../../../../css/cards.css";
import "../../../../css/buttons.css";
import "../../../../css/layout.css";
import "../../../../css/tables.css";

import { api } from "../../../../api/apiClient";
import PageHeader from "../../../../app/layout/PageHeader";
import FrontlineDataGrid from "../../../../components/common/FrontlineDataGrid";
import "./CampaignReportPage.css";

type StatusFilter = "all" | "delivered" | "sent" | "pending" | "failed";

interface CampaignExecutionReport {
  campaignExecutionId: number;
  status: string;
  executionDate?: string | null;
  startedDate?: string | null;
  completedDate?: string | null;
  errorMessage?: string | null;
}

interface CampaignStatistics {
  totalRecipients: number;
  delivered: number;
  sent: number;
  pending: number;
  failed: number;
}

interface CampaignRecipientReport {
  partyId: number;
  recipientName: string;
  mobileNumber: string;
  messageBody: string;
  status: string;
  providerUpdatedDate?: string | null;
}

interface CampaignReport {
  campaignId: number;
  campaignName: string;
  status: string;
  createdDate: string;
  createdBy?: string | null;
  messageBody: string;
  execution?: CampaignExecutionReport | null;
  statistics: CampaignStatistics;
  recipients: CampaignRecipientReport[];
}

const formatDateTime = (value?: string | null) =>
  value ? new Date(value).toLocaleString() : "";

const getStatus = (status?: string | null) => status?.toLowerCase() ?? "";

const isDelivered = (status?: string | null) => getStatus(status) === "delivered";

const isSent = (status?: string | null) => getStatus(status) === "sent";

const isPending = (status?: string | null) => {
  const normalized = getStatus(status);
  return (
    normalized === "pending" ||
    normalized === "queued" ||
    normalized === "processing"
  );
};

const isFailed = (status?: string | null) => {
  const normalized = getStatus(status);
  return (
    normalized === "failed" ||
    normalized === "rejected" ||
    normalized === "undeliverable"
  );
};

export default function CampaignReportPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const history = useHistory();
  const [report, setReport] = useState<CampaignReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  useEffect(() => {
    let isMounted = true;

    const loadReport = async () => {
      setLoading(true);
      setError(null);

      try {
        const data = await api<CampaignReport>(
          `/api/campaign/${campaignId}/report`,
          { method: "GET" },
        );

        if (isMounted) setReport(data);
      } catch {
        if (isMounted) {
          setReport(null);
          setError("Failed to load campaign report");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadReport();

    return () => {
      isMounted = false;
    };
  }, [campaignId]);

  const statistics = report?.statistics ?? {
    totalRecipients: 0,
    delivered: 0,
    sent: 0,
    pending: 0,
    failed: 0,
  };
  const filteredRecipients = useMemo(() => {
    const term = search.trim().toLowerCase();

    return (report?.recipients ?? []).filter((recipient) => {
      const matchesSearch =
        !term ||
        `${recipient.recipientName} ${recipient.mobileNumber} ${recipient.status}`
          .concat(` ${recipient.messageBody}`)
          .toLowerCase()
          .includes(term);

      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "delivered" && isDelivered(recipient.status)) ||
        (statusFilter === "sent" && isSent(recipient.status)) ||
        (statusFilter === "pending" && isPending(recipient.status)) ||
        (statusFilter === "failed" && isFailed(recipient.status));

      return matchesSearch && matchesStatus;
    });
  }, [report?.recipients, search, statusFilter]);

  const rows = useMemo(
    () =>
      filteredRecipients.map((recipient, index) => ({
        id: `${recipient.partyId}-${index}`,
        ...recipient,
      })),
    [filteredRecipients],
  );

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "recipientName", headerName: "Recipient", flex: 1, minWidth: 180 },
      { field: "mobileNumber", headerName: "Mobile", width: 150 },
      { field: "messageBody", headerName: "Message", flex: 1.4, minWidth: 240 },
      { field: "status", headerName: "Status", width: 140 },
      {
        field: "providerUpdatedDate",
        headerName: "Updated",
        width: 180,
        valueFormatter: (params) =>
          params.value ? formatDateTime(params.value as string) : "Waiting",
      },
    ],
    [],
  );

  const execution = report?.execution;

  return (
    <div className="page-container">
      <PageHeader
        title="Campaign Report"
        subtitle="Delivery and processing details"
        actions={
          <button
            className="icon-button"
            onClick={() => history.push("/manage-campaigns")}
            title="Back to Campaigns"
          >
            <ArrowBackIcon fontSize="small" />
          </button>
        }
      />

      {error && <div className="form-error">{error}</div>}

      {!error && !loading && !report && (
        <div className="card">No campaign report found.</div>
      )}

      {report && (
        <>
          <section className="card campaign-report-overview">
            <div className="card-title">{report.campaignName}</div>

            <div className="campaign-report-message">
              <div className="summary-name">Message</div>
              <div className="summary-value">
                {report.messageBody || "No message body recorded."}
              </div>
            </div>

            <div className="campaign-report-meta">
              <SummaryItem label="Status" value={report.status} />
              <SummaryItem
                label="Sent"
                value={
                  formatDateTime(
                    execution?.startedDate ?? execution?.executionDate,
                  ) ||
                  "Not started"
                }
              />
              <SummaryItem
                label="Created"
                value={formatDateTime(report.createdDate)}
              />
              <SummaryItem
                label="Created By"
                value={report.createdBy || "Not recorded"}
              />
            </div>

            <div
              className="campaign-report-metrics"
              aria-label="Delivery summary"
            >
              <MetricCard
                label="Recipients"
                value={statistics.totalRecipients}
              />
              <MetricCard label="Sent" value={statistics.sent} />
              <MetricCard label="Delivered" value={statistics.delivered} />
              <MetricCard label="Pending" value={statistics.pending} />
              <MetricCard label="Failed" value={statistics.failed} />
            </div>
          </section>

          {execution?.errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {execution.errorMessage}
            </Alert>
          )}

          <section className="card campaign-report-delivery">
            <div className="card-title">Recipient Delivery</div>

            <div className="campaign-report-delivery-toolbar">
              <div className="search-container table-search">
                <div className="search-input-wrapper">
                  <SearchIcon className="search-icon-left" fontSize="small" />
                  <input
                    className="form-input search-input-with-icons"
                    placeholder="Search recipients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button
                      type="button"
                      className="search-clear-button"
                      onClick={() => setSearch("")}
                      aria-label="Clear recipient search"
                    >
                      <CloseIcon fontSize="small" />
                    </button>
                  )}
                </div>
              </div>
              <label className="campaign-report-status-filter">
                <span>Status</span>
                <select
                  className="form-input"
                  value={statusFilter}
                  onChange={(event) =>
                    setStatusFilter(event.target.value as StatusFilter)
                  }
                >
                  <option value="all">All ({statistics.totalRecipients})</option>
                  <option value="delivered">
                    Delivered ({statistics.delivered})
                  </option>
                  <option value="sent">Sent ({statistics.sent})</option>
                  <option value="pending">Pending ({statistics.pending})</option>
                  <option value="failed">Failed ({statistics.failed})</option>
                </select>
              </label>
              <span className="campaign-report-result-count">
                {filteredRecipients.length} shown
              </span>
            </div>

            <div className="table-container">
              <FrontlineDataGrid
                loading={loading}
                rows={rows}
                columns={columns}
                localeText={{ noRowsLabel: "No recipients found" }}
              />
            </div>
          </section>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="campaign-report-metric">
      <div className="summary-name">{label}</div>
      <div className="summary-value">{value}</div>
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="campaign-report-meta-item">
      <div className="summary-name">{label}</div>
      <div className="summary-value">{value}</div>
    </div>
  );
}
